import { getExpiredMediaPreviewText, isMediaExpiredMarker } from './mediaRetention.ts';
import { parseInstagramComment } from './instagramComment.ts';

export type LastMessagePreview = {
  type: 'text' | 'audio' | 'video' | 'image' | 'document' | 'media';
  text: string;
};

const mediaLabels = { audio: 'Áudio', video: 'Vídeo', image: 'Imagem', document: 'Documento', media: 'Mídia' };
const aliases: Record<string, keyof typeof mediaLabels> = {
  audio: 'audio', ptt: 'audio', voice: 'audio', video: 'video', image: 'image',
  document: 'document', file: 'document', sticker: 'image',
};
const isTransport = (value: string) => /^(?:https?:\/\/|data:|\/api\/|waha\/|\/?.*arquivos\/media\/)/i.test(value.trim());

export interface StoredWhatsAppLocation {
  latitude: number;
  longitude: number;
  name: string;
  address: string;
}

export const parseStoredWhatsAppLocation = (content: unknown): StoredWhatsAppLocation | null => {
  let value = content;
  if (typeof value === 'string') {
    try { value = JSON.parse(value); } catch { return null; }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const location = value as Record<string, unknown>;
  if (
    location.latitude == null
    || location.longitude == null
    || typeof location.latitude === 'boolean'
    || typeof location.longitude === 'boolean'
    || (typeof location.latitude === 'string' && !location.latitude.trim())
    || (typeof location.longitude === 'string' && !location.longitude.trim())
  ) return null;
  const latitude = Number(location.latitude);
  const longitude = Number(location.longitude);
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)
    || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    return null;
  }
  return {
    latitude,
    longitude,
    name: typeof location.name === 'string' ? location.name : '',
    address: typeof location.address === 'string' ? location.address : '',
  };
};

/** Prefer the persisted message type; URL inference only supports legacy records. */
export const formatLastMessage = (content?: string | null, messageType?: string | null): LastMessagePreview => {
  const raw = content?.trim() || '';
  if (isMediaExpiredMarker(raw)) return { type: 'text', text: getExpiredMediaPreviewText() };
  const type = messageType?.trim().toLowerCase();
  if (type === 'comment') {
    const comment = parseInstagramComment(raw);
    return { type: 'text', text: comment ? `${comment.is_public_reply ? 'Resposta pública' : 'Comentário'}: ${comment.text}` : 'Comentário do Instagram' };
  }

  let structured: Record<string, unknown> | undefined;
  if (raw.startsWith('{')) {
    try { const value = JSON.parse(raw); if (value && typeof value === 'object') structured = value; } catch { /* Plain message text. */ }
  }
  if (type === 'nps' || structured?.nps_data || structured?.nps_id) {
    return { type: 'text', text: '📊 Pesquisa NPS' };
  }
  if (type === 'location') {
    const location = parseStoredWhatsAppLocation(raw);
    if (location) {
      const description = location.name || location.address || 'Localização compartilhada';
      return {
        type: 'text',
        text: `📍 ${description}${location.name && location.address ? ` · ${location.address}` : ''}`,
      };
    }
    return { type: 'text', text: '📍 Localização compartilhada' };
  }
  if (type === 'text' || type === 'chat') return { type: 'text', text: content || '' };
  let mediaType = type ? aliases[type] : undefined;
  if (!type) {
    const marker = raw.match(/^\[(AUDIO|VIDEO|IMAGE|DOCUMENT)\]$/i)?.[1]?.toLowerCase()
      || raw.match(/^(audio|video|image|document)_message$/)?.[1];
    mediaType = marker ? aliases[marker] : undefined;
    if (!mediaType) {
      const dataType = raw.match(/^data:(audio|video|image)\//i)?.[1]?.toLowerCase();
      mediaType = dataType ? aliases[dataType] : undefined;
    }
    const reference = typeof structured?.url === 'string' ? structured.url : raw;
    if (!mediaType && /^(?:https?:\/\/|\/api\/|waha\/)/i.test(reference)) {
      const path = reference.split(/[?#]/)[0];
      if (/\.(mp3|wav|ogg|m4a|opus)$/i.test(path)) mediaType = 'audio';
      else if (/\.(mp4|mov|avi|webm)$/i.test(path)) mediaType = 'video';
      else if (/\.(jpg|jpeg|png|gif|webp)$/i.test(path)) mediaType = 'image';
      else if (/\.(pdf|docx?|xlsx?|pptx?|zip)$/i.test(path)) mediaType = 'document';
      else if (/^(?:https?:\/\/[^/]+)?\/api\/(?:waha\/media|arquivos\/media)\//i.test(reference) || reference.startsWith('waha/')) mediaType = 'media';
    }
  }
  if (!mediaType) return { type: 'text', text: content || '' };
  const caption = typeof structured?.caption === 'string' ? structured.caption.trim() : '';
  return { type: mediaType, text: caption && !isTransport(caption) ? `${mediaLabels[mediaType]}: ${caption}` : mediaLabels[mediaType] };
};
